import { createFileRoute, useLocation, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { MyCardsSort } from '@molt/shared';
import { Button, EmptyState, Skeleton } from '@/components';
import { MyCardsPage } from '@/components/personal/MyCardsPage';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { myCardsQueryOptions } from '@/lib/personal-cards';
import { signInHref, useSession } from '@/lib/session';
import { workspacesQueryOptions } from '@/lib/workspaces';

export const Route = createFileRoute('/my/cards')({
  component: MyCardsRoute,
});

function MyCardsRoute() {
  const location = useLocation();
  const router = useRouter();
  const { t } = useInterfaceLanguage();
  const session = useSession();
  const signedIn = !session.isSignedOut && !session.isPending && session.profile !== null;
  const params = new URLSearchParams(location.searchStr);
  const workspaceId = params.get('workspace') ?? undefined;
  const sort = params.get('sort') === 'board' ? 'board' : 'due';
  const cards = useQuery({
    ...myCardsQueryOptions({ workspaceId, sort }),
    enabled: signedIn,
  });
  const workspaces = useQuery({ ...workspacesQueryOptions(), enabled: signedIn });

  function replaceSearch(next: { workspaceId?: string | undefined; sort?: MyCardsSort }) {
    const search = new URLSearchParams(location.searchStr);
    if (next.workspaceId === undefined) {
      search.delete('workspace');
    } else {
      search.set('workspace', next.workspaceId);
    }
    if (next.sort === undefined || next.sort === 'due') {
      search.delete('sort');
    } else {
      search.set('sort', next.sort);
    }
    const query = search.toString();
    router.history.replace(`${location.pathname}${query === '' ? '' : `?${query}`}`);
    router.history.flush();
  }

  if (session.isSignedOut) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <EmptyState
          tone="neutral"
          size="panel"
          title={t('myCards.signInTitle')}
          body={t('myCards.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              href={signInHref({ pathname: '/my/cards', search: location.searchStr })}
            >
              {t('myCards.signInAction')}
            </Button>
          }
        />
      </main>
    );
  }

  if (session.isPending) {
    return (
      <main className="mx-auto grid w-full max-w-5xl gap-3 px-4 py-6 sm:px-6">
        <Skeleton shape="block" />
        <Skeleton shape="row" />
        <Skeleton shape="row" />
      </main>
    );
  }

  if (session.profile === null) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <EmptyState
          tone="error"
          size="panel"
          title={t('myCards.loadFailedTitle')}
          body={t('myCards.genericError')}
        />
      </main>
    );
  }

  return (
    <MyCardsPage
      cards={cards.data?.cards ?? []}
      workspaces={workspaces.data ?? []}
      workspaceId={workspaceId}
      sort={sort}
      loading={cards.isPending || workspaces.isPending}
      error={cards.error?.message ?? workspaces.error?.message ?? null}
      onWorkspaceChange={(nextWorkspaceId) =>
        replaceSearch({ workspaceId: nextWorkspaceId, sort })
      }
      onSortChange={(nextSort) => replaceSearch({ workspaceId, sort: nextSort })}
    />
  );
}
